import { Yup } from 'shared/yup-extended';
import { RequiredStringSchema } from 'yup/lib/string';
import { API } from 'api';
import { isEmailSSORequired } from 'features/sso/hooks/useSSORequired';

export type EmailStatus = 'available' | 'registered' | 'managed';

export const getEmailStatus = async (
  email: string,
  cache: Map<string, EmailStatus>,
): Promise<EmailStatus> => {
  if (cache.has(email)) {
    return cache.get(email)!;
  }
  const response = await API.get<{ hasActiveMembership: boolean }>(
    `/recurly_integration/membership_validation/?email=${encodeURIComponent(
      email,
    )}`,
  );
  let result: EmailStatus = 'available';

  const isAvailable = !response.data.hasActiveMembership;
  if (!isAvailable) {
    result = 'registered';
  } else {
    const ssoRequired = await isEmailSSORequired(email);
    if (ssoRequired) {
      result = 'managed';
    }
  }
  cache.set(email, result);
  return result;
};

export const getEmailValidationSchema = (input: {
  cache: Map<string, EmailStatus>;
  skip: boolean;
  requiredErrMsg: string;
  invalidErrMsg: string;
  registeredErrMsg: string;
  managedErrMsg: string;
  skipAvailabilityCheck?: boolean;
}): RequiredStringSchema<string | undefined, Record<string, unknown>> => {
  const {
    cache,
    skip,
    requiredErrMsg,
    invalidErrMsg,
    registeredErrMsg,
    managedErrMsg,
    skipAvailabilityCheck = false,
  } = input;
  return Yup.string()
    .required(requiredErrMsg)
    .test({
      name: 'is-email-available',
      // eslint-disable-next-line object-shorthand
      test: async function (value) {
        if (!value || skip) {
          return true;
        }

        try {
          Yup.string()
            .email(invalidErrMsg)
            .validTLD(invalidErrMsg)
            .validateSync(value);
        } catch (error) {
          const err = error as Yup.ValidationError;
          return this.createError({ message: err.message, type: err.type });
        }

        if (skipAvailabilityCheck) {
          return true;
        }

        const emailStatus = await getEmailStatus(value, cache);
        if (emailStatus === 'registered') {
          return this.createError({
            message: registeredErrMsg,
            type: 'registered',
          });
        }
        if (emailStatus === 'managed') {
          return this.createError({
            message: managedErrMsg,
            type: 'managed',
          });
        }

        return true;
      },
    });
};
